import { HAS_API, requestJson } from './apiConfig'
import { DEFAULT_ADMIN_SLUG } from '../data/reservedRoutes'
import type {
  DatabaseSettings,
  DatabaseTestResult,
  InstallRequest,
  InstallResult,
  InstallStatus,
  PublicPlatformConfig,
  SystemCheck,
} from '../types/install'

/**
 * The installation seam.
 *
 * Every function here is a request to the server. The browser performs no part
 * of an installation: it does not decide whether the platform is installed, it
 * does not test a database, it does not hash a password and it does not write
 * configuration. It collects input, shows what the server reports, and refuses
 * to pretend when there is nothing to talk to.
 *
 *   GET  /api/install/status
 *   POST /api/install/check-system      → issues the installation token
 *   POST /api/install/test-database
 *   POST /api/install/run              → migrations, admin, configuration
 *   GET  /api/config/platform          → public runtime configuration
 */

const STATUS = '/api/install/status'
const CHECK = '/api/install/check-system'
const TEST_DB = '/api/install/test-database'
const RUN = '/api/install/run'
const PLATFORM = '/api/config/platform'

/**
 * The short-lived token the server issues after system checks. It is held in
 * memory for the life of the wizard only — never in storage, because a token
 * that survives a closed tab is a token someone else can find.
 */
let installToken: string | null = null

function tokenHeaders(): Record<string, string> {
  return installToken ? { 'X-Campuzon-Install-Token': installToken } : {}
}

/** True when this deployment has a configuration service to install against. */
export const INSTALLER_IS_CONNECTED = HAS_API

export class NoServiceError extends Error {
  constructor() {
    super(
      'No Campuzon API is configured for this deployment, so there is nothing to install against. Set the API origin first — see INSTALLATION.md.',
    )
    this.name = 'NoServiceError'
  }
}

export async function status(): Promise<InstallStatus> {
  if (!HAS_API) throw new NoServiceError()
  return requestJson<InstallStatus>(STATUS)
}

export async function checkSystem(): Promise<{ checks: SystemCheck[]; token: string }> {
  if (!HAS_API) throw new NoServiceError()
  const result = await requestJson<{ checks: SystemCheck[]; token: string }>(CHECK, {
    method: 'POST',
  })
  installToken = result.token
  return result
}

export async function testDatabase(settings: DatabaseSettings): Promise<DatabaseTestResult> {
  if (!HAS_API) throw new NoServiceError()
  // The password travels in the body, over TLS, once. It is never placed in a
  // URL, never returned, and never logged by the endpoint.
  return requestJson<DatabaseTestResult>(TEST_DB, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...tokenHeaders() },
    body: JSON.stringify(settings),
  })
}

export async function install(request: InstallRequest): Promise<InstallResult> {
  if (!HAS_API) throw new NoServiceError()
  return requestJson<InstallResult>(RUN, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...tokenHeaders() },
    body: JSON.stringify(request),
  })
}

/** Discards the installation token. Called when the wizard is finished with. */
export function forgetToken(): void {
  installToken = null
}

/**
 * The configuration the product runs on. Falls back to development defaults so
 * a deployment without a configuration service still renders — but the values
 * are labelled as defaults wherever they are shown, never as configuration.
 */
export const DEVELOPMENT_CONFIG: PublicPlatformConfig = {
  installed: false,
  platform: {
    name: 'Campuzon',
    shortName: 'Campuzon',
    organization: 'Campuzon Technologies',
    domain: '',
    supportEmail: '',
    timezone: 'Africa/Lagos',
    currency: 'NGN',
    country: 'NG',
    locale: 'en-NG',
  },
  seo: {
    siteTitle: 'Campuzon',
    metaTitle: 'Campuzon — your campus, connected',
    metaDescription:
      'Find housing and people you can live with, manage your academics, and reach opportunities around your campus.',
    siteUrl: '',
    socialTitle: 'Campuzon',
    socialDescription: 'Housing, academics, community and opportunities around your campus.',
    socialImageUrl: '',
    faviconUrl: '',
    logoUrl: '',
    robots: 'index,follow',
  },
  localization: {
    timezone: 'Africa/Lagos',
    currency: 'NGN',
    locale: 'en-NG',
    country: 'NG',
    dateFormat: 'd MMM yyyy',
    timeFormat: '24h',
    measurement: 'metric',
    weekStartsOn: 'monday',
  },
  adminSlug: DEFAULT_ADMIN_SLUG,
  schemaVersion: null,
  environment: 'development',
}

export async function loadPlatformConfig(): Promise<{
  config: PublicPlatformConfig
  source: 'server' | 'defaults'
}> {
  if (!HAS_API) return { config: DEVELOPMENT_CONFIG, source: 'defaults' }
  try {
    const config = await requestJson<PublicPlatformConfig>(PLATFORM)
    // A response missing the fields the product needs is not usable
    // configuration, so it is treated as absent rather than partly applied.
    if (!config?.platform?.name || !config?.seo?.metaTitle || !config?.adminSlug) {
      return { config: DEVELOPMENT_CONFIG, source: 'defaults' }
    }
    return { config, source: 'server' }
  } catch {
    return { config: DEVELOPMENT_CONFIG, source: 'defaults' }
  }
}
